import React, { FunctionComponent } from 'react'
import { Link, GatsbyLinkProps } from 'gatsby'
import { t } from '@lingui/macro'

import { i18n } from 'utils/i18n'
import { getPaginatedPathSuffix } from 'utils/paginate'
import ControlIcon from 'components/icons/ControlIcon'

import { calcRenderablePaginationList } from './calc-pagination'
import PaginationMetatags from './metatags'
import {
  navRoot,
  paginationList,
  paginationItem,
  previousPageLink,
  nextPageLink,
  nextPageIcon,
  previousPageIcon,
  pageLink,
} from './index.module.scss'

const getPath = (basePath: string, page: number) =>
  `${basePath}${getPaginatedPathSuffix(page)}`

interface PreviousNextButtonProps
  extends Omit<GatsbyLinkProps<unknown>, 'ref'> {
  disabled: boolean
  to: string
}

const PreviousNextButton: FunctionComponent<PreviousNextButtonProps> = ({
  disabled,
  to,
  ...props
}) => {
  return disabled ? (
    <span aria-hidden="true" aria-current="true" {...props} />
  ) : (
    <Link aria-current="false" to={to} {...props} />
  )
}

interface PaginationProps {
  currentPage: number
  totalPageCount: number
  basePath: string
  withMetatags: boolean
}

const Pagination = ({
  currentPage,
  totalPageCount,
  basePath,
  withMetatags,
}: PaginationProps) => {
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPageCount
  return (
    <nav className={navRoot} aria-label={i18n._(t`Pagination Navigation`)}>
      {withMetatags ? (
        <PaginationMetatags
          currentPage={currentPage}
          totalPageCount={totalPageCount}
          basePath={basePath}
        />
      ) : null}
      <ul className={paginationList}>
        <li className={paginationItem}>
          <PreviousNextButton
            disabled={isFirstPage}
            to={getPath(basePath, currentPage - 1)}
            className={previousPageLink}
            aria-label={i18n._(t`Go to previous page`)}
          >
            <ControlIcon iconName="chevron-left" className={previousPageIcon} />
          </PreviousNextButton>
        </li>
        {calcRenderablePaginationList({
          currentPage,
          totalPageCount,
        }).map(({ isOmitSign, isCurrentPage, pageNr, omitSign, key }) => (
          <li key={key} className={paginationItem}>
            {isOmitSign ? (
              <span>{omitSign}</span>
            ) : (
              <Link
                to={getPath(basePath, pageNr)}
                className={pageLink}
                aria-label={
                  isCurrentPage
                    ? i18n._(t`Go to current page, page ${pageNr}`)
                    : i18n._(t`Go to page ${pageNr}`)
                }
                aria-current={isCurrentPage}
              >
                {pageNr}
              </Link>
            )}
          </li>
        ))}
        <li className={paginationItem}>
          <PreviousNextButton
            disabled={isLastPage}
            to={getPath(basePath, currentPage + 1)}
            className={nextPageLink}
            aria-label={i18n._(t`Go to next page`)}
          >
            <ControlIcon iconName="chevron-right" className={nextPageIcon} />
          </PreviousNextButton>
        </li>
      </ul>
    </nav>
  )
}

export default Pagination
